import { atom, atomFamily, selector, selectorFamily, useRecoilState, useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil"
import { userState, useUser } from "."
import { User } from "../models"
import { GroupDescriptor } from "../models/GroupDescriptor"
import { ServiceDescriptor } from "../models/ServiceDescriptor"
import { AmbienteSelector, CodiceDescrizione, ProvinciaSelector, UserContext, UserContextSelector } from "../models/UserContext"
import apiService from "../services/api"
import { areeMySixtemaState } from "../utils/hooks"
import { userContext } from "./auth"
import { serviceRequestNumberState } from "./serviceDescriptor"

export const groupDescriptorsState = selector({
    key: 'groupDescriptorsState',
    get: () => apiService.getGroupDesciptors()
})

export const groupDescriptorDicState = selector({
    key: 'groupDescriptorDicState',
    get: ({ get }) => {
        const gd = get(groupDescriptorsState)
        const rv: Record<number, GroupDescriptor> = {}
        if (gd) {
            gd.forEach(g => rv[g.id] = g)
        }
        return rv
    }
})

export const groupDescriptorAreaDicState = selector({
    key: 'groupDescriptorAreaDicState',
    get: ({ get }) => {
        const gd = get(groupDescriptorsState)
        const rv: Record<string, GroupDescriptor> = {}
        if (gd) {
            gd.forEach(g => rv[g.area] = g)
        }
        return rv
    }
})

export const useGroupDescriptors = () => {
    const dictionary = useRecoilValue(groupDescriptorDicState)
    const areaDictionary = useRecoilValue(groupDescriptorAreaDicState)
    const array = useRecoilValue(groupDescriptorsState)
    return { dictionary, areaDictionary, array }
}

export type AreaLevelType = { area: string, level: string }
export type AreaLevelLabelType = { area: string, levelLabel: string }

const sdSezioni = ['Applicazioni', 'Circolari', 'Link Utili']

export const sdAllState = selector({
    key: 'sdAllState',
    get: ({ get }) => {
        const user = get(userState)
        let aree = get(areeMySixtemaState)
        // console.log(`selector: sdAllState: ${user?.name} ${aree.join(' ')}`, user)
        if (!user) return []
        if (user.role === 'azienda') aree = ['azienda']
        return apiService.getAllServiceDesciptorsFilterAree(aree)
    }
})

export type ServiceDescriptorGrouped = {
    label: string,
    items: ServiceDescriptor[]
}
const levelsOrder = {
    'Sixtema': 0,
    'Nazionale': 1,
    'Regionale': 2,
    'Provincia': 3,
    'Ufficio': 4,
    'Azienda': 5,
}
const compareLevels = (a: ServiceDescriptor, b: ServiceDescriptor) => levelsOrder[a.livello] - levelsOrder[b.livello]
const groupByUrlDescriptionAndNote = (sdAll: ServiceDescriptor[] | null) => {
    let rv: ServiceDescriptorGrouped[] = []
    if (sdAll) {
        const temp: Record<string, ServiceDescriptor[]> = {}
        sdAll.forEach(sd => {
            const label = `${sd.urlDescription} - ${sd.note} [${sd.livello}]`
            const present = temp[label]
            if (temp[label]) {
                if(!(present[0].guid === sd.guid && present[0].url === sd.url)) {
                    temp[label].push(sd)
                }
            } else {
                temp[label] = [sd]
            }
        })
        rv = Object.entries(temp)
            .map(x => ({ label: x[0], items: x[1].sort(compareLevels) }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    return rv
}

export const sdAllGroupedState = selector({
    key: 'sdAllGroupedState',
    get: ({ get }) => {
        const sdAll = get(sdAllState)
        return groupByUrlDescriptionAndNote(sdAll)
    }
})

export const useSdAllGrouped = () => {
    const allSd = useRecoilValue(sdAllGroupedState)
    // La seguente è una dipendenza che se non soddisfatta a questo punto porta guai :)
    useRecoilValue(preferitiSdMapState)
    return allSd
}

export const sdForGroupState = selectorFamily({
    key: 'sdForGroupState',
    get: (area: string) => ({ get }) => {
        const groupDescriptors = get(groupDescriptorsState)
        get(serviceRequestNumberState)
        get(userContext)
        if (groupDescriptors) {
            const gd = groupDescriptors.find(g => g.area === area)
            if (gd) {
                // console.log(`Reading: getAppsSdForGroup(${gd.id}) [${gd.area}]`)
                return apiService.getAppsSdForGroup(gd.id)
                    .then(data => {
                        // console.log(`Letto Gruppo ${gd.id}: ${gd.area}`, data)
                        if (!data) return
                        for (const key of Object.keys(data)) {
                            const sd = data[key]
                            sdSezioni.forEach((sez, index) => { sd[sez] = sd.items.filter(x => x.sezione === sez) })
                        }
                        return data
                    })
            }
        }
    }
})
export const levelMenuState = selectorFamily({
    key: 'levelMenuState',
    get: ({ area, levelLabel }: AreaLevelLabelType) => ({ get }) => {
        // console.log(`levelMenuState: ${area} - ${levelLabel}`)
        const sdGroup = get(sdForGroupState(area))
        // console.log(sdGroup)
        if (sdGroup) {
            const sds = sdGroup[levelLabel]
            return sds
        }
    }
})
export const contextSelector = selector({
    key: 'contextSelect',
    get: ({ get }) => createContextSelector(get(userContext))
})
export const userCtxUfficioCorrente = selector({
    key: 'userCtxUfficioCorrente',
    get: ({ get }) => getCodiceDescrizionePerUfficioCorrente(get(userState), get(contextSelector))
})
export const useUserContextView = () => {
    const user = useUser()
    const userCtx = useRecoilValue(userCtxUfficioCorrente)
    return { user, userCtx }
}
export const useContextSelector = () => {
    return useRecoilValue(contextSelector)
}
const pselProv = (u: string) => u.split('.')[1].substr(0, 2)
const pselAmbi = (u: string) => u.split('.')[0]
export type CtxUfficioCorrenteType = {
    ufficioCorrente: string
    provSel: ProvinciaSelector
    ambiSel: AmbienteSelector
    uffiSel: CodiceDescrizione
    userCtx: UserContext,
}
const getCodiceDescrizionePerUfficioCorrente = (user: User | null, userCtxSel: UserContextSelector) => {
    const userCtx = user?.context;
    const isAzienda = user?.role === 'azienda'
    // per la gestione dell'utente aziendale dobbiamo tornare una struttura con dati vuoti, ma non nulli
    // altrimenti nel resto dell'applicazione scoppia tutto :(
    if (isAzienda && userCtx) {
        return {
            ufficioCorrente: userCtx.codice,
            provSel: { ambienti: [], codice: '', descrizione: '', value: '', label: '' },
            ambiSel: { uffici: [], codice: '', descrizione: '', value: '', label: '' },
            uffiSel: { codice: '', descrizione: '', value: '', label: '' },
            userCtx: userCtx,
        } as CtxUfficioCorrenteType
    } else if (userCtx?.ufficioCorrente) {
        let p = userCtxSel.province[0]
        let a = p.ambienti[0]
        let u = a.uffici[0]
        const pcode = userCtx.provincia !== '00' ? pselProv(userCtx.ufficioCorrente) : userCtx.provincia
        p = userCtxSel.province.find(x => x.codice === pcode) || p
        a = p.ambienti.find(x => x.codice === pselAmbi(userCtx.ufficioCorrente)) || a
        u = a.uffici.find(x => x.codice === userCtx.ufficioCorrente) || u
        return {
            ufficioCorrente: userCtx.ufficioCorrente,
            provSel: p,
            ambiSel: a,
            uffiSel: u,
            userCtx: userCtx,
        } as CtxUfficioCorrenteType
    }
}
const createContextSelector = (uc: UserContext | null) => {
    if (!uc) return { province: [] }
    const rv: UserContextSelector = {
        province: uc.province.map(p => ({
            codice: p.codiceDb,
            descrizione: p.descrizione,
            value: p.value,
            label: p.label,
            ambienti: p.ambienti.map(a => ({
                codice: a,
                value: a,
                descrizione: a,
                label: a,
                uffici: p.uffici.filter(fu => fu.codice.startsWith(a)),
            }))
        }))
    }
    // console.log(`createContextSelector`)
    return rv;
}

export const preferitiRequestNumberState = atom({
    key: 'preferitiRequestNumberState',
    default: 1
})

export const linksRequestNumberState = atom({
    key: 'linksRequestNumberState',
    default: 1
})

export const preferitiState = selector({
    key: 'preferitiState',
    get: ({ get }) => {
        var preferitiRequestNumber = get(preferitiRequestNumberState)
        var userctx = get(userContext)
        if (!userctx) return null
        return apiService.getPreferiti(userctx?.name, preferitiRequestNumber)
    }
})

export const linksState = selector({
    key: 'linksState',
    get: ({ get }) => {
        var linksRequestNumber = get(linksRequestNumberState)
        var userctx = get(userContext)
        if (!userctx) return null
        return apiService.getLinks(userctx?.name, linksRequestNumber)
    }
})

export const preferitiSdMapState = selector({
    key: 'preferitiDicState',
    get: ({ get }) => { 
        var preferiti = get(preferitiState)
        const sdlist = preferiti?.map(p => p.serviceDescriptor)
        return sdlist?.reduce<Map<number, ServiceDescriptor>>((prev, curr) => prev.set(curr.id, curr), new Map<number, ServiceDescriptor>())
    }
})

export const usePreferitoForSdExists = () => {
    const prefMap = useRecoilValue(preferitiSdMapState)
    return (id: number) => prefMap?.get(id) 
}

type SegmentStatusType = {
    name: string,
    open: boolean,
}

export const segmentStatusAtom = atomFamily<SegmentStatusType, string>({
    key: 'segmentStatusAtom',
    default: (name: string) => ({
        name,
        open: true,
    })
})

export const useSegmentStatus = (name: string) => {
    const rv = useRecoilState(segmentStatusAtom(name))
    return rv
}

const historyTxState = atom({
    key: 'HistoryTxState',
    default: 0
})

export type OrderType = 'date' | 'appl' | 'nrun'
export type HistoryOptions = {
    order: OrderType,
    nrow: number
}

const initialHistoryOptions: HistoryOptions = {
    order: 'date',
    nrow: 20,
}

export const historyOptionsState = atom({
    key: 'historyOptionsState',
    default: initialHistoryOptions
})

const historyState = selector({
    key: 'HistoryState',
    get: ({ get }) => {
        get(historyTxState)
        const options = get(historyOptionsState)
        return apiService.getHistoryWithOptions(options.order, options.nrow)
    }
})

export const useMyHistory = () => {
    const historyLoadable = useRecoilValueLoadable(historyState)
    const setRefreshHistory = useSetRecoilState(historyTxState)
    const refresh = (timeout: number = 500) => {
        setTimeout(() => setRefreshHistory(p => p + 1), timeout)
    }
    const purge = (keep: number, order: OrderType) => apiService.deleteHistoryWithOptions(order, keep)
    const truncate = () => apiService.deleteHistory()
    return { historyLoadable, refresh, purge, truncate }
}
